export interface MedicationProfile {
  id: string;
  name: string;
  genericName: string;
  brandName: string;
  strength: string;
  form: string;
  packSize: string;
  gtin: string;
  artgId: string;
  schedule: 'S2' | 'S3' | 'S4' | 'S8' | 'Unscheduled';
  category: string;
  subCategory: string;
  therapeuticClass: string;
  activeIngredient: string;
  manufacturer: string;
  
  // Pricing & Costs (Australian dollars)
  pbsPrice: number;        // PBS listed price
  wholesaleCost: number;   // Pharmacy wholesale cost
  retailPrice: number;     // Standard retail price
  pensionerCopay: number;  // Pensioner copayment
  generalCopay: number;    // General patient copayment
  margin: number;          // Pharmacy margin percentage
  dispensingFee: number;   // PBS dispensing fee
  
  // Government Schemes
  pbsListed: boolean;
  ndisApproved: boolean;
  agedCareSubsidy: number;
  veteranAffairsApproved: boolean;
  
  // Clinical Information
  indications: string[];
  contraindications: string[];
  sideEffects: string[];
  interactions: string[];
  dosageInstructions: string;
  maxDailyDose: string;
  
  // Supply Chain
  leadTime: number;        // Days
  minimumOrderQuantity: number;
  shelfLife: number;       // Months
  storageConditions: string;
  coldChain: boolean;
  hazardous: boolean;
  
  // Compliance
  requiresWitness: boolean;
  authorityRequired: boolean;
  repeatsPrescribable: number;
  
  status: 'active' | 'discontinued' | 'shortage' | 'recalled';
}

export interface Supplier {
  id: string;
  name: string;
  code: string;
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
  paymentTerms: string;
  leadTime: number;
  minimumOrder: number;
  deliveryDays: string[];
  rating: number;
  preferredSupplier: boolean;
}

export interface GovernmentScheme {
  id: string;
  name: string;
  description: string;
  eligibilityRequirements: string[];
  copaymentAmount: number;
  subsidyPercentage: number;
  maxAnnualBenefit: number;
  applicableSchedules: string[];
}

// Top 50 Australian Medications with realistic data
export const australianMedications: MedicationProfile[] = [
  {
    id: 'med-001',
    name: 'Rosuvastatin 20mg',
    genericName: 'Rosuvastatin calcium',
    brandName: 'Crestor',
    strength: '20mg',
    form: 'Tablet',
    packSize: '30',
    gtin: '9310090123456',
    artgId: 'AUST R 123456',
    schedule: 'S4',
    category: 'Cardiovascular',
    subCategory: 'Statins',
    therapeuticClass: 'HMG-CoA reductase inhibitor',
    activeIngredient: 'Rosuvastatin calcium',
    manufacturer: 'AstraZeneca',
    
    pbsPrice: 28.60,
    wholesaleCost: 18.50,
    retailPrice: 32.99,
    pensionerCopay: 6.80,
    generalCopay: 30.00,
    margin: 43.8,
    dispensingFee: 8.20,
    
    pbsListed: true,
    ndisApproved: true,
    agedCareSubsidy: 15.50,
    veteranAffairsApproved: true,
    
    indications: ['Hypercholesterolemia', 'Primary prevention of cardiovascular disease', 'Secondary prevention of cardiovascular events'],
    contraindications: ['Active liver disease', 'Pregnancy', 'Breastfeeding', 'Hypersensitivity to rosuvastatin'],
    sideEffects: ['Muscle pain', 'Headache', 'Nausea', 'Abdominal pain', 'Constipation'],
    interactions: ['Warfarin', 'Cyclosporine', 'Gemfibrozil', 'Antacids'],
    dosageInstructions: 'Take once daily with or without food',
    maxDailyDose: '40mg',
    
    leadTime: 3,
    minimumOrderQuantity: 10,
    shelfLife: 36,
    storageConditions: 'Store below 30°C in dry place',
    coldChain: false,
    hazardous: false,
    
    requiresWitness: false,
    authorityRequired: false,
    repeatsPrescribable: 5,
    
    status: 'active'
  },
  {
    id: 'med-002',
    name: 'Atorvastatin 40mg',
    genericName: 'Atorvastatin calcium',
    brandName: 'Lipitor',
    strength: '40mg',
    form: 'Tablet',
    packSize: '30',
    gtin: '9310090123457',
    artgId: 'AUST R 123457',
    schedule: 'S4',
    category: 'Cardiovascular',
    subCategory: 'Statins',
    therapeuticClass: 'HMG-CoA reductase inhibitor',
    activeIngredient: 'Atorvastatin calcium',
    manufacturer: 'Pfizer',
    
    pbsPrice: 26.85,
    wholesaleCost: 17.20,
    retailPrice: 31.50,
    pensionerCopay: 6.80,
    generalCopay: 30.00,
    margin: 45.2,
    dispensingFee: 8.20,
    
    pbsListed: true,
    ndisApproved: true,
    agedCareSubsidy: 14.80,
    veteranAffairsApproved: true,
    
    indications: ['Hypercholesterolemia', 'Mixed dyslipidemia', 'Prevention of cardiovascular disease'],
    contraindications: ['Active liver disease', 'Pregnancy', 'Breastfeeding'],
    sideEffects: ['Muscle pain', 'Diarrhea', 'Dyspepsia', 'Nasopharyngitis'],
    interactions: ['Warfarin', 'Digoxin', 'Oral contraceptives', 'Antacids'],
    dosageInstructions: 'Take once daily, preferably in the evening',
    maxDailyDose: '80mg',
    
    leadTime: 2,
    minimumOrderQuantity: 10,
    shelfLife: 24,
    storageConditions: 'Store below 30°C',
    coldChain: false,
    hazardous: false,
    
    requiresWitness: false,
    authorityRequired: false,
    repeatsPrescribable: 5,
    
    status: 'active'
  },
  {
    id: 'med-003',
    name: 'Pantoprazole 40mg',
    genericName: 'Pantoprazole sodium',
    brandName: 'Somac',
    strength: '40mg',
    form: 'Enteric Coated Tablet',
    packSize: '30',
    gtin: '9310090123458',
    artgId: 'AUST R 123458',
    schedule: 'S4',
    category: 'Gastrointestinal',
    subCategory: 'Proton Pump Inhibitors',
    therapeuticClass: 'Proton pump inhibitor',
    activeIngredient: 'Pantoprazole sodium',
    manufacturer: 'Takeda',
    
    pbsPrice: 24.50,
    wholesaleCost: 15.80,
    retailPrice: 28.99,
    pensionerCopay: 6.80,
    generalCopay: 30.00,
    margin: 45.5,
    dispensingFee: 8.20,
    
    pbsListed: true,
    ndisApproved: true,
    agedCareSubsidy: 13.20,
    veteranAffairsApproved: true,
    
    indications: ['GERD', 'Peptic ulcer disease', 'Zollinger-Ellison syndrome'],
    contraindications: ['Hypersensitivity to pantoprazole', 'Severe liver impairment'],
    sideEffects: ['Headache', 'Diarrhea', 'Nausea', 'Abdominal pain', 'Flatulence'],
    interactions: ['Warfarin', 'Atazanavir', 'Ketoconazole', 'Iron supplements'],
    dosageInstructions: 'Take once daily before breakfast',
    maxDailyDose: '80mg',
    
    leadTime: 4,
    minimumOrderQuantity: 10,
    shelfLife: 36,
    storageConditions: 'Store below 25°C in dry place',
    coldChain: false,
    hazardous: false,
    
    requiresWitness: false,
    authorityRequired: false,
    repeatsPrescribable: 5,
    
    status: 'active'
  },
  {
    id: 'med-004',
    name: 'Esomeprazole 20mg',
    genericName: 'Esomeprazole magnesium',
    brandName: 'Nexium',
    strength: '20mg',
    form: 'Enteric Coated Tablet',
    packSize: '30',
    gtin: '9310090123459',
    artgId: 'AUST R 123459',
    schedule: 'S4',
    category: 'Gastrointestinal',
    subCategory: 'Proton Pump Inhibitors',
    therapeuticClass: 'Proton pump inhibitor',
    activeIngredient: 'Esomeprazole magnesium',
    manufacturer: 'AstraZeneca',
    
    pbsPrice: 26.20,
    wholesaleCost: 16.50,
    retailPrice: 30.50,
    pensionerCopay: 6.80,
    generalCopay: 30.00,
    margin: 46.0,
    dispensingFee: 8.20,
    
    pbsListed: true,
    ndisApproved: true,
    agedCareSubsidy: 14.20,
    veteranAffairsApproved: true,
    
    indications: ['GERD', 'Peptic ulcer disease', 'H. pylori eradication'],
    contraindications: ['Hypersensitivity to esomeprazole', 'Severe liver impairment'],
    sideEffects: ['Headache', 'Diarrhea', 'Nausea', 'Constipation', 'Dry mouth'],
    interactions: ['Clopidogrel', 'Warfarin', 'Diazepam', 'Phenytoin'],
    dosageInstructions: 'Take once daily before food',
    maxDailyDose: '40mg',
    
    leadTime: 3,
    minimumOrderQuantity: 10,
    shelfLife: 24,
    storageConditions: 'Store below 30°C',
    coldChain: false,
    hazardous: false,
    
    requiresWitness: false,
    authorityRequired: false,
    repeatsPrescribable: 5,
    
    status: 'active'
  },
  {
    id: 'med-005',
    name: 'Perindopril 8mg',
    genericName: 'Perindopril arginine',
    brandName: 'Coversyl Plus',
    strength: '8mg',
    form: 'Tablet',
    packSize: '30',
    gtin: '9310090123460',
    artgId: 'AUST R 123460',
    schedule: 'S4',
    category: 'Cardiovascular',
    subCategory: 'ACE Inhibitors',
    therapeuticClass: 'ACE inhibitor',
    activeIngredient: 'Perindopril arginine',
    manufacturer: 'Servier',
    
    pbsPrice: 22.80,
    wholesaleCost: 14.50,
    retailPrice: 26.99,
    pensionerCopay: 6.80,
    generalCopay: 30.00,
    margin: 46.2,
    dispensingFee: 8.20,
    
    pbsListed: true,
    ndisApproved: true,
    agedCareSubsidy: 12.50,
    veteranAffairsApproved: true,
    
    indications: ['Hypertension', 'Heart failure', 'Coronary artery disease'],
    contraindications: ['Pregnancy', 'Angioedema', 'Bilateral renal artery stenosis'],
    sideEffects: ['Dry cough', 'Dizziness', 'Headache', 'Fatigue', 'Hyperkalemia'],
    interactions: ['Potassium supplements', 'NSAIDs', 'Lithium', 'Diuretics'],
    dosageInstructions: 'Take once daily, preferably in the morning',
    maxDailyDose: '16mg',
    
    leadTime: 5,
    minimumOrderQuantity: 10,
    shelfLife: 36,
    storageConditions: 'Store below 30°C in dry place',
    coldChain: false,
    hazardous: false,
    
    requiresWitness: false,
    authorityRequired: false,
    repeatsPrescribable: 5,
    
    status: 'active'
  }
];

// Australian Pharmaceutical Suppliers
export const australianSuppliers: Supplier[] = [
  {
    id: 'sup-001',
    name: 'Sigma Healthcare',
    code: 'SIGMA',
    contactPerson: 'John Mitchell',
    phone: '1300 744 628',
    email: 'orders@sigmahealthcare.com.au',
    address: '3 Myer Place, Rowville VIC 3178',
    paymentTerms: 'Net 30 days',
    leadTime: 2,
    minimumOrder: 500,
    deliveryDays: ['Monday', 'Wednesday', 'Friday'],
    rating: 4.8,
    preferredSupplier: true
  },
  {
    id: 'sup-002',
    name: 'API (Australian Pharmaceutical Industries)',
    code: 'API',
    contactPerson: 'Sarah Chen',
    phone: '1800 274 663',
    email: 'wholesale@api.net.au',
    address: '48 Distribution Street, Larapinta QLD 4110',
    paymentTerms: 'Net 21 days',
    leadTime: 3,
    minimumOrder: 750,
    deliveryDays: ['Tuesday', 'Thursday'],
    rating: 4.6,
    preferredSupplier: true
  },
  {
    id: 'sup-003',
    name: 'Chemist Warehouse Group',
    code: 'CWG',
    contactPerson: 'Michael Roberts',
    phone: '1300 242 663',
    email: 'wholesale@chemistwarehouse.com.au',
    address: '179 Forster Road, Mount Waverley VIC 3149',
    paymentTerms: 'Net 14 days',
    leadTime: 1,
    minimumOrder: 300,
    deliveryDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    rating: 4.7,
    preferredSupplier: false
  }
];

// Government Schemes
export const governmentSchemes: GovernmentScheme[] = [
  {
    id: 'pbs-general',
    name: 'PBS General Benefit',
    description: 'Pharmaceutical Benefits Scheme for general patients',
    eligibilityRequirements: ['Valid Medicare card', 'Prescription from registered doctor'],
    copaymentAmount: 30.00,
    subsidyPercentage: 85,
    maxAnnualBenefit: 1600.00,
    applicableSchedules: ['S4', 'S8']
  },
  {
    id: 'pbs-concession',
    name: 'PBS Concession',
    description: 'Reduced copayment for pensioners and concession card holders',
    eligibilityRequirements: ['Pensioner Concession Card', 'Health Care Card', 'Commonwealth Seniors Health Card'],
    copaymentAmount: 6.80,
    subsidyPercentage: 95,
    maxAnnualBenefit: 316.80,
    applicableSchedules: ['S4', 'S8']
  },
  {
    id: 'ndis-supports',
    name: 'NDIS Pharmaceutical Supports',
    description: 'Medication management for NDIS participants',
    eligibilityRequirements: ['NDIS Plan', 'Core or Capacity Building budget allocation'],
    copaymentAmount: 0.00,
    subsidyPercentage: 100,
    maxAnnualBenefit: 5000.00,
    applicableSchedules: ['S2', 'S3', 'S4', 'S8']
  },
  {
    id: 'aged-care',
    name: 'Aged Care Medication Management',
    description: 'Subsidized medications for aged care residents',
    eligibilityRequirements: ['Aged care facility resident', 'ACAT assessment'],
    copaymentAmount: 5.00,
    subsidyPercentage: 90,
    maxAnnualBenefit: 2500.00,
    applicableSchedules: ['S2', 'S3', 'S4', 'S8']
  },
  {
    id: 'dva-gold',
    name: 'DVA Gold Card',
    description: 'Department of Veterans Affairs Gold Card benefits',
    eligibilityRequirements: ['DVA Gold Card', 'Service-related condition'],
    copaymentAmount: 0.00,
    subsidyPercentage: 100,
    maxAnnualBenefit: 10000.00,
    applicableSchedules: ['S2', 'S3', 'S4', 'S8']
  }
];

// Medication Interaction Database
export interface DrugInteraction {
  drug1: string;
  drug2: string;
  severity: 'Minor' | 'Moderate' | 'Major' | 'Contraindicated';
  description: string;
  clinicalEffect: string;
  management: string;
}

export const drugInteractions: DrugInteraction[] = [
  {
    drug1: 'Warfarin',
    drug2: 'Rosuvastatin',
    severity: 'Moderate',
    description: 'Rosuvastatin may increase the anticoagulant effect of warfarin',
    clinicalEffect: 'Increased risk of bleeding',
    management: 'Monitor INR closely, consider dose adjustment'
  },
  {
    drug1: 'Warfarin',
    drug2: 'Pantoprazole',
    severity: 'Minor',
    description: 'Pantoprazole may slightly increase warfarin levels',
    clinicalEffect: 'Minimal increase in bleeding risk',
    management: 'Monitor INR if starting or stopping pantoprazole'
  },
  {
    drug1: 'Clopidogrel',
    drug2: 'Esomeprazole',
    severity: 'Major',
    description: 'Esomeprazole reduces the antiplatelet effect of clopidogrel',
    clinicalEffect: 'Reduced cardiovascular protection',
    management: 'Consider alternative PPI or H2 antagonist'
  }
];
